/**
 * ComponentLoader - Chargement paresseux des styles de composants
 * Optimise les performances en chargeant uniquement les CSS nécessaires
 */

export class ComponentLoader {
  constructor() {
    this.loadedComponents = new Set();
    this.componentMap = new Map();
    this.setupComponentMap();
  }

  /**
   * Initialisation du loader
   */
  async init() {}

  /**
   * Configuration de la carte des composants (CSS + JS)
   */
  setupComponentMap() {
    // Atoms - CSS et JS
    this.componentMap.set("atom-button", {
      css: () => import("../../scss/blocks/atoms/button-atom.scss"),
      js: () => import("../blocks/atoms/button-atom.js"),
    });
    this.componentMap.set("paragraph-button", {
      css: () => import("../../scss/blocks/atoms/button-atom.scss"),
      js: () => import("../blocks/atoms/button-atom.js"),
    });

    // Molecules - CSS et JS
    this.componentMap.set("molecule-hero", {
      css: () => import("../../scss/blocks/molecules/hero-molecule.scss"),
      js: () => import("../blocks/molecules/hero-molecule.js"),
    });
    this.componentMap.set("paragraph-hero", {
      css: () => import("../../scss/blocks/molecules/hero-molecule.scss"),
      js: () => import("../blocks/molecules/hero-molecule.js"),
    });

    // Organisms - CSS et JS

    // Composants spéciaux
    // NOTE: other component mappings (card, cta, organisms, gallery, etc.)
    // were removed because their SCSS files are not present in the
    // repository yet. Add them back when those files exist.
  }

  /**
   * Charge les styles des composants présents dans un élément
   */
  async loadComponentStyles(element) {
    // Trouver tous les composants dans l'élément
    const components = this.findComponents(element);

    // Charger les styles pour chaque composant
    for (const componentType of components) {
      await this.loadComponent(componentType);
    }
  }

  /**
   * Trouve les composants dans un élément
   */
  findComponents(element) {
    const components = new Set();

    // Vérifier l'élément lui-même
    this.componentMap.forEach((loader, selector) => {
      if (element.matches && element.matches(`.${selector}`)) {
        components.add(selector);
      }

      // Vérifier les enfants
      const children = element.querySelectorAll(`.${selector}`);
      if (children.length > 0) {
        components.add(selector);
      }
    });

    // Détection par attributs data
    const dataComponents = element.querySelectorAll("[data-component]");
    dataComponents.forEach((comp) => {
      const componentType = comp.dataset.component;
      if (this.componentMap.has(componentType)) {
        components.add(componentType);
      }
    });

    // Détection par classes atomic
    const atomicElements = element.querySelectorAll('[class*="atomic-"]');
    atomicElements.forEach((elem) => {
      const classes = elem.className.split(" ");
      classes.forEach((className) => {
        if (className.startsWith("paragraph-")) {
          const componentType = className;
          if (this.componentMap.has(componentType)) {
            components.add(componentType);
          }
        }
      });
    });

    return components;
  }

  /**
   * Charge un composant spécifique (CSS + JS)
   */
  async loadComponent(componentType, loadType = "both") {
    // Créer une clé unique pour le type de chargement
    const loadKey = `${componentType}-${loadType}`;

    // Ne pas charger deux fois le même composant avec le même type
    if (this.loadedComponents.has(loadKey)) {
      return;
    }

    const componentConfig = this.componentMap.get(componentType);
    if (!componentConfig) {
      console.warn(`No loader found for component: ${componentType}`);
      return;
    }

    try {
      const loadPromises = [];

      // Charger le CSS si demandé
      if ((loadType === "both" || loadType === "css") && componentConfig.css) {
        loadPromises.push(
          componentConfig.css().catch((error) => {
            console.warn(`CSS not found for ${componentType}:`, error.message);
          }),
        );
      }

      // Charger le JS si demandé
      if ((loadType === "both" || loadType === "js") && componentConfig.js) {
        loadPromises.push(
          componentConfig.js().catch((error) => {
            console.warn(`JS not found for ${componentType}:`, error.message);
          }),
        );
      }

      // Attendre le chargement de tous les assets
      await Promise.allSettled(loadPromises);

      this.loadedComponents.add(loadKey);

      // Déclencher un événement personnalisé
      document.dispatchEvent(
        new CustomEvent("component-loaded", {
          detail: { componentType, loadType },
        }),
      );
    } catch (error) {
      console.error(`Error loading component ${componentType}:`, error);
    }
  }

  /**
   * Précharge des composants critiques
   */
  async preloadCriticalComponents() {
    const critical = ["atom-button", "molecule-hero", "organism-header"];

    for (const component of critical) {
      await this.loadComponent(component);
    }
  }

  /**
   * Attache le loader au contexte Drupal
   */
  attach(context) {
    this.loadComponentStyles(context);
  }

  /**
   * Méthode utilitaire pour charger manuellement un composant
   */
  static async loadComponentManually(componentType) {
    const instance = new ComponentLoader();
    await instance.loadComponent(componentType);
  }
}

// Export également une instance globale
window.componentLoader = new ComponentLoader();
